import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Newspaper,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Sparkles,
  BarChart3,
  TrendingUp,
  Target,
  Users,
  Award,
  Globe,
  Clock,
  ShieldCheck,
  Check,
  Building2,
  FileCheck,
  Layers,
  Search,
  BookOpen,
  Calendar,
  FileText,
  Bookmark,
} from "lucide-react";

export const Route = createFileRoute("/services_/print")({
  head: () => ({
    meta: [
      {
        title:
          "Print Advertising Services — KMWAdvertising · Newspapers & Magazines",
      },
      {
        name: "description",
        content:
          "Print Advertising Services That Build Credibility, Visibility and Lasting Brand Impact. Newspaper & Magazine Media Planning, Front-Page Jackets & Buying by KMWA LLP.",
      },
      {
        property: "og:title",
        content: "Print Advertising Services — KMWAdvertising",
      },
      {
        property: "og:description",
        content:
          "Strategic Print Advertising Solutions for Businesses That Want to Stand Out. National, regional & local newspaper media buying.",
      },
    ],
  }),
  component: PrintServicePage,
});

export function PrintServicePage() {
  const printBenefits = [
    {
      title: "High Credibility",
      desc: "Consumers often perceive advertisements in respected newspapers and magazines as more trustworthy than many online advertisements. Appearing alongside quality editorial content enhances brand reputation and reinforces credibility.",
      icon: ShieldCheck,
    },
    {
      title: "Longer Shelf Life",
      desc: "Magazine advertisements may remain visible for weeks or even months, while newspapers are frequently shared among households, offices and public spaces, extending campaign reach beyond the initial publication date.",
      icon: Clock,
    },
    {
      title: "Targeted Audience Reach",
      desc: "Specialised publications allow brands to reach highly relevant audiences based on interests, profession, geography or demographics.",
      icon: Target,
    },
    {
      title: "Local and Regional Penetration",
      desc: "Print advertising is particularly effective for businesses targeting specific cities, districts or regional markets where local newspapers continue to enjoy strong readership.",
      icon: Globe,
    },
    {
      title: "Strong Brand Recall",
      desc: "Large-format advertisements, premium layouts and high-quality visuals create memorable impressions that strengthen brand awareness and support long-term recall.",
      icon: Sparkles,
    },
  ];

  const newspaperFormats = [
    "National newspaper advertising",
    "Regional newspaper campaigns",
    "Local newspaper advertising",
    "Classified advertising",
    "Display advertisements",
    "Jacket advertisements",
    "Innovative advertisements",
    "Front-page advertising",
    "Back-page advertising",
    "Full-page advertisements",
    "Half-page advertisements",
    "Quarter-page advertisements",
    "Government tenders & public notices",
    "Recruitment advertising",
  ];

  const mediaPlanningResearchPoints = [
    "Age groups & demographics",
    "Occupation & industry focus",
    "Education level",
    "Household income brackets",
    "Purchase behaviour & intent",
    "Geographic location & city tiers",
    "Language preferences & regional dialects",
    "Media consumption habits",
  ];

  const publicationSelectionCriteria = [
    "Readership profiles & audit figures",
    "Circulation data (ABC verified)",
    "Geographic reach & regional editions",
    "Editorial quality & publication affinity",
    "Audience demographic alignment",
    "Publication frequency (Daily, Weekly, Monthly)",
    "Industry relevance & vertical focus",
    "Special festive & seasonal editions",
  ];

  const scheduleTimingTriggers = [
    "Product & Service Launches",
    "Retail & E-commerce Sales Promotions",
    "Festive Season Campaigns",
    "Industry Events & Trade Shows",
    "Corporate Recruitment Drives",
    "Financial Year-End Campaigns",
    "Educational Admissions Campaigns",
    "Real Estate Project Launches",
  ];

  const printBuyingServices = [
    "Space booking across major publications",
    "Competitive rate negotiations",
    "Position selection (Right-hand, Page 3, Front Jacket)",
    "Premium placement buying",
    "Color advertisement booking",
    "Multi-publication package deals",
    "National & regional campaign buying",
    "Release order (RO) management",
    "Artwork technical coordination",
    "Voucher & publication verification",
  ];

  return (
    <div className="dark relative min-h-screen bg-background text-foreground flex flex-col w-full overflow-x-clip">
      <SiteNav />

      <main className="flex-1 w-full overflow-x-clip pt-24 md:pt-28">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-14 sm:py-20 md:py-24 border-b border-white/10">
          <div className="absolute inset-0 grid-bg opacity-15" aria-hidden />
          <div className="glow-orb -left-32 top-10 h-[450px] w-[450px] bg-[var(--glow-orange)] opacity-25" />
          <div className="glow-orb right-[-100px] top-20 h-[400px] w-[400px] bg-[var(--glow-purple)] opacity-25" />

          <div className="container-x relative z-10">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2.5 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Link to="/" className="hover:text-white transition">
                Home
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <Link to="/services" className="hover:text-white transition">
                Services
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-[var(--glow-orange)] font-semibold">Print Advertising</span>
            </div>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground border border-white/10 mb-6">
                <Newspaper className="h-3.5 w-3.5 text-[var(--glow-orange)]" />
                Traditional Media Capabilities
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-white">
                Print Advertising Services That Build Credibility, Visibility and Lasting Brand Impact
              </h1>

              <p className="mt-6 text-lg sm:text-xl font-accent italic text-[var(--glow-orange)] font-medium border-l-2 border-[var(--glow-orange)] pl-4 py-1">
                Strategic Print Advertising Solutions for Businesses That Want to Stand Out
              </p>

              <div className="mt-6 space-y-4 text-base sm:text-lg text-white/90 font-normal leading-relaxed">
                <p>
                  In a world dominated by digital channels, print advertising continues to hold a unique position in the marketing mix. Newspapers & magazines remain trusted sources of information for millions of readers, making print an effective medium for building credibility, reaching targeted audiences and reinforcing brand presence.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  For many industries, including retail, healthcare, education, automotive, real estate, finance and government, print advertising delivers a level of trust and permanence that digital platforms often struggle to match. A well-designed newspaper advertisement or magazine campaign can capture attention, communicate complex information and remain visible long after publication.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  At <strong className="text-white">KMWA LLP</strong>, we provide end-to-end print advertising solutions that combine strategic planning, creative excellence and data-driven media buying. From selecting the right publications and negotiating competitive advertising rates to designing impactful print creatives and measuring campaign effectiveness, our team manages every stage of the process.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <a href="/contact-us" className="btn-hero !py-3.5 !px-7">
                  Request Print Media Proposal
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/services" className="btn-ghost !py-3.5 !px-7">
                  Explore All Services
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* WHY PRINT MATTERS & BENEFITS */}
        <section className="py-14 sm:py-20 border-b border-white/10 bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)] block mb-2">
                Unmatched Authority
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                Why Print Advertising Still Matters
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">
                Despite the growth of digital marketing, print remains a highly effective communication channel for businesses seeking credibility, longevity and targeted audience engagement. Newspapers and magazines are often read multiple times, shared among family members or colleagues, and retained for future reference. This extended exposure increases brand recall and message retention.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {printBenefits.map((benefit, idx) => {
                const BIcon = benefit.icon;
                return (
                  <div
                    key={idx}
                    className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 flex flex-col justify-between group"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)]">
                          BENEFIT 0{idx + 1}
                        </span>
                        <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                          <BIcon className="h-5 w-5" />
                        </div>
                      </div>
                      <h3 className="font-display text-xl font-bold text-white mb-2">
                        {benefit.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {benefit.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* OUR PRINT ADVERTISING SERVICES */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)] block mb-2">
                Tailored Print Execution
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Our Print Advertising Services
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-2">
                Comprehensive print advertising solutions tailored to your marketing objectives, audience profile and advertising budget.
              </p>
            </div>

            {/* NEWSPAPER ADVERTISING */}
            <div className="rounded-3xl glass-strong border border-white/15 p-6 sm:p-10 md:p-12 mb-16 relative overflow-hidden shadow-2xl">
              <div className="glow-orb -top-32 -right-32 h-96 w-96 rounded-full bg-[var(--glow-orange)] opacity-15" />

              <div className="grid lg:grid-cols-12 gap-8 items-start relative z-10">
                <div className="lg:col-span-5 space-y-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-[var(--glow-orange)] bg-[var(--glow-orange)]/10 border border-[var(--glow-orange)]/30">
                    <Newspaper className="h-3.5 w-3.5" />
                    Broad Visibility
                  </div>
                  <h3 className="font-display text-2xl sm:text-3xl font-bold text-white">
                    Newspaper Advertising
                  </h3>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
                    Reach Local, Regional and National Audiences with Precision
                  </p>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Newspapers continue to be one of the most trusted advertising platforms for businesses seeking broad visibility and regional market penetration. We help clients advertise across leading national, regional and local newspapers to ensure maximum audience relevance.
                  </p>
                </div>

                <div className="lg:col-span-7 bg-[#06060c]/90 rounded-2xl p-6 border border-white/10">
                  <h4 className="text-xs font-mono uppercase tracking-widest text-white/80 mb-4 block">
                    Our Newspaper Formats & Options Include:
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {newspaperFormats.map((format, fIdx) => (
                      <div
                        key={fIdx}
                        className="flex items-center gap-2.5 p-2.5 rounded-xl bg-white/[0.03] border border-white/5 text-xs text-white/90"
                      >
                        <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                        <span>{format}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* PRINT MEDIA PLANNING */}
            <div className="mb-16">
              <div className="border-b border-white/10 pb-4 mb-8">
                <h3 className="font-display text-2xl font-bold text-white flex items-center gap-2.5">
                  <BarChart3 className="h-5 w-5 text-[var(--glow-orange)]" />
                  Print Media Planning
                </h3>
                <p className="text-xs sm:text-sm font-mono text-muted-foreground uppercase tracking-wider mt-1">
                  Strategic Planning That Maximises Advertising Performance
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {/* Audience Research */}
                <div className="p-6 rounded-2xl glass-strong border border-white/10 space-y-4">
                  <h4 className="font-display text-lg font-bold text-white flex items-center gap-2">
                    <Users className="h-4 w-4 text-[var(--glow-orange)]" />
                    Audience Research
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Understanding your customers helps us identify the publications they trust and regularly consume:
                  </p>
                  <div className="space-y-1.5">
                    {mediaPlanningResearchPoints.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                        <Check className="h-3 w-3 text-emerald-400 shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Publication Selection */}
                <div className="p-6 rounded-2xl glass-strong border border-white/10 space-y-4">
                  <h4 className="font-display text-lg font-bold text-white flex items-center gap-2">
                    <BookOpen className="h-4 w-4 text-[var(--glow-orange)]" />
                    Publication Selection
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Choosing the right publication is critical to campaign success. We systematically analyze:
                  </p>
                  <div className="space-y-1.5">
                    {publicationSelectionCriteria.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                        <Check className="h-3 w-3 text-emerald-400 shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Schedule Planning */}
                <div className="p-6 rounded-2xl glass-strong border border-white/10 space-y-4">
                  <h4 className="font-display text-lg font-bold text-white flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-[var(--glow-orange)]" />
                    Schedule Planning
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Timing plays a crucial role in print performance. We align print insertions with key business triggers:
                  </p>
                  <div className="space-y-1.5">
                    {scheduleTimingTriggers.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                        <Check className="h-3 w-3 text-emerald-400 shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* PRINT MEDIA BUYING */}
            <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 relative overflow-hidden shadow-2xl">
              <div className="grid lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-5 space-y-4">
                  <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)] block">
                    Strategic Rate Negotiation
                  </span>
                  <h3 className="font-display text-2xl sm:text-3xl font-bold text-white">
                    Print Media Buying
                  </h3>
                  <p className="text-sm font-accent italic text-white/90 border-l-2 border-[var(--glow-orange)] pl-3">
                    Secure Premium Placements at Competitive Rates
                  </p>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Media buying is about more than purchasing advertising space. It requires strong publisher relationships, market knowledge and strategic negotiation to maximize value.
                  </p>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Our experienced media buying team works directly with newspaper publishers and magazine houses to secure premium placements while optimising advertising budgets.
                  </p>
                </div>

                <div className="lg:col-span-7 bg-[#06060c]/90 rounded-2xl p-6 border border-white/10">
                  <h4 className="text-xs font-mono uppercase tracking-widest text-white/80 mb-4 block">
                    Our Media Buying Services Include:
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {printBuyingServices.map((service, sIdx) => (
                      <div
                        key={sIdx}
                        className="flex items-center gap-2.5 p-3 rounded-xl bg-white/[0.03] border border-white/5 text-xs text-white/90"
                      >
                        <ShieldCheck className="h-4 w-4 text-[var(--glow-orange)] shrink-0" />
                        <span>{service}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CLOSING CTA BANNER */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="glow-orb left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] bg-[var(--glow-orange)] opacity-20" />

          <div className="container-x relative z-10 text-center max-w-4xl">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-muted-foreground border border-white/10 mb-6">
              <Newspaper className="h-3.5 w-3.5 text-[var(--glow-orange)]" />
              Print Media Advisory
            </div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              Ready to Build Trust & High Impact with Print Advertising?
            </h2>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Connect with KMWA LLP's print media buying and planning specialists to build high-credibility print campaigns tailored to your target audience.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/contact-us" className="btn-hero w-full sm:w-auto">
                Request Print Proposal
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/services" className="btn-ghost w-full sm:w-auto">
                Back to All Services
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
